import { useState, useCallback, useMemo } from 'react';
import type { Player } from '../types/player';

interface RecentPick {
  pick: number;
  teamId: number;
  playerName: string;
  position: string;
  team: string;
  timestamp: Date;
}

interface DraftState {
  currentPick: number;
  userTeamId: number;
  userRoster: Player[];
  availablePlayers: Player[];
  draftedPlayers: Set<string>;
  recentPicks: RecentPick[];
  draftStatus: 'inactive' | 'starting' | 'active' | 'ending';
}

interface UseDraftStateProps {
  teamCount: number;
  draftPosition: number;
  allPlayers: Player[];
}

export function useDraftState({ 
  teamCount, 
  draftPosition, 
  allPlayers 
}: UseDraftStateProps) {
  const [currentPick, setCurrentPick] = useState(1);
  const [userRoster, setUserRoster] = useState<Player[]>([]);
  const [draftedPlayerIds, setDraftedPlayerIds] = useState<Set<string>>(new Set());
  const [recentPicks, setRecentPicks] = useState<RecentPick[]>([]);
  const [draftStatus, setDraftStatus] = useState<'inactive' | 'starting' | 'active' | 'ending'>('inactive');

  // Calculate current picking state
  const { currentTeam, round, pickInRound, isUserTurn } = useMemo(() => {
    const round = Math.floor((currentPick - 1) / teamCount) + 1;
    const pickInRound = ((currentPick - 1) % teamCount) + 1;
    
    // Snake draft logic
    const currentTeam = round % 2 === 1 
      ? pickInRound 
      : teamCount - pickInRound + 1;
    
    const isUserTurn = draftStatus === 'active' && currentTeam === draftPosition;

    return { currentTeam, round, pickInRound, isUserTurn };
  }, [currentPick, teamCount, draftPosition, draftStatus]);

  // Available players (not drafted)
  const availablePlayers = useMemo(() => {
    return allPlayers.filter(player => !draftedPlayerIds.has(String(player.id)));
  }, [allPlayers, draftedPlayerIds]);

  // Draft a player
  const draftPlayer = useCallback((player: Player) => {
    console.log(`Drafting player: ${player.name} for team ${currentTeam} (pick ${currentPick})`);
    
    // Add to drafted players
    setDraftedPlayerIds(prev => new Set([...prev, String(player.id)]));
    
    // Add to user roster if it's their pick
    if (isUserTurn) {
      setUserRoster(prev => [...prev, player]);
    }
    
    // Add to recent picks
    const newPick: RecentPick = {
      pick: currentPick,
      teamId: currentTeam,
      playerName: player.name,
      position: player.position,
      team: player.team || '',
      timestamp: new Date()
    };
    
    setRecentPicks(prev => {
      const updated = [newPick, ...prev.slice(0, 9)];
      console.log(`Updated recent picks:`, updated.map(p => `${p.pick}: ${p.playerName} (T${p.teamId})`));
      return updated;
    });
    
    // Advance to next pick
    setCurrentPick(prev => prev + 1);
  }, [currentPick, currentTeam, isUserTurn]);

  // Add a recent pick (for CPU picks)
  const addRecentPick = useCallback((pick: RecentPick) => {
    setRecentPicks(prev => [pick, ...prev.slice(0, 9)]);
  }, []);

  // Advance to next pick
  const advancePick = useCallback(() => {
    setCurrentPick(prev => prev + 1);
  }, []);

  // Start draft
  const startDraft = useCallback(() => {
    setDraftStatus('active');
    setCurrentPick(1);
    setUserRoster([]);
    setDraftedPlayerIds(new Set());
    setRecentPicks([]);
  }, []);

  // Reset draft
  const resetDraft = useCallback(() => {
    setDraftStatus('inactive');
    setCurrentPick(1);
    setUserRoster([]);
    setDraftedPlayerIds(new Set());
    setRecentPicks([]);
  }, []);

  // Check if position can be drafted (placeholder for future roster construction rules)
  const canDraftPosition = useCallback((_position: string) => {
    // For now, allow all positions - could add roster construction logic here
    return true;
  }, []);

  const draftState: DraftState = {
    currentPick,
    userTeamId: draftPosition,
    userRoster,
    availablePlayers,
    draftedPlayers: draftedPlayerIds,
    recentPicks,
    draftStatus
  };

  return {
    draftState,
    currentTeam,
    round,
    pickInRound,
    isUserTurn,
    draftPlayer,
    addRecentPick,
    advancePick,
    startDraft,
    resetDraft,
    setDraftStatus,
    canDraftPosition
  };
}